import React, { useEffect, useState } from 'react'
import { useGlobalState } from '@/state'
import { formatInTimezone } from '@/utils/date'
import { CalendarIcon, ArrowRightIcon } from '@heroicons/react/solid'

import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'

const SECTION_CLASS = 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'
const H2_CLASS = 'text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100'

const CourseCard = ({cohort, currentUser, past}) => (
  <a
    href={`/c/${cohort.courseSlug}`}
    className={`group bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-md shadow-slate-900/[0.05] hover:shadow-xl hover:shadow-clcpurple/20 hover:border-clcpurple/40 hover:-translate-y-0.5 hover:no-underline transition-all duration-300 ${past ? 'opacity-90' : ''}`}
  >
    <div className="aspect-w-16 aspect-h-9 overflow-hidden">
      <img className="object-cover w-full transition-transform duration-500 group-hover:scale-105" src={cohort.imageUrl} alt={cohort.name} />
    </div>
    <div className="p-5">
      <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 leading-snug group-hover:text-clcpurple dark:group-hover:text-clcpurple-light transition-colors">{cohort.name}</h3>
      <div className="mt-3 flex items-center text-sm text-slate-500 dark:text-slate-400">
        <CalendarIcon className='w-4 h-4 mr-1.5 shrink-0' />
        <span>{formatInTimezone(cohort.startsAtUtc, currentUser.timezone, 'MMM d')} – {formatInTimezone(cohort.endsAtUtc, currentUser.timezone, 'MMM d, Y')}</span>
      </div>
      <div className="mt-4 inline-flex items-center text-sm font-bold text-clcblue dark:text-clcnavy-light group-hover:text-clcnavy-dark transition-colors">
        {past ? 'Revisit course' : 'View course'}
        <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </div>
  </a>
)

const MyCourses = (props) => {
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const [ enrolledCohorts, setEnrolledCohorts ] = useState([])
  const [ pastCohorts, setPastCohorts ] = useState([])
  const [ loading, setLoading ] = useState(false)

  useEffect(() => {
    setLoading(true)
    getRequest(`/api/v1/users/dashboard`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setEnrolledCohorts(jsonData.enrolledCohorts)
      setPastCohorts(jsonData.pastCohorts)
      setLoading(false)
    })
  }, [])

  const empty = !loading && enrolledCohorts.length === 0 && pastCohorts.length === 0

  return <div className='py-8 space-y-8'>
    { loading && <Loading /> }

    { empty && (
      <section className={`${SECTION_CLASS} text-center`}>
        <div className="max-w-md mx-auto py-4">
          <div className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600">
            <svg fill="none" stroke="currentColor" viewBox="0 0 48 48" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C12 4.56 13.79 3 16.253 3h15.494C34.21 3 36 4.56 36 6.253v13M12 6.253L8.224 9.44c-.707.707-.707 1.853 0 2.56L12 15.747m24-9.494L39.776 9.44c.707.707.707 1.853 0 2.56L36 15.747m-24 0v21.494C12 39.44 13.79 41 16.253 41h15.494C34.21 41 36 39.44 36 37.247V15.747M12 15.747l4-4h16l4 4" />
            </svg>
          </div>
          <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-slate-100">No courses yet</h3>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            You haven't enrolled in any courses yet. Browse our available courses to start learning.
          </p>
          <a
            href="/courses"
            className="mt-5 inline-flex items-center justify-center px-6 py-3 text-sm font-bold rounded-lg text-white bg-clcblue shadow-md hover:bg-clcnavy-dark hover:text-white hover:shadow-lg transition-all"
          >
            Browse courses
            <ArrowRightIcon className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>
    )}

    { enrolledCohorts.length > 0 && (
      <section className={SECTION_CLASS}>
        <h2 className={H2_CLASS}>Courses I'm currently taking</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {enrolledCohorts.map((cohort) => (
            <CourseCard key={`dasco${cohort.id}`} cohort={cohort} currentUser={currentUser} />
          ))}
        </div>
      </section>
    )}

    { pastCohorts.length > 0 && (
      <section className={SECTION_CLASS}>
        <h2 className={H2_CLASS}>Courses I've taken</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {pastCohorts.map((cohort) => (
            <CourseCard key={`past${cohort.id}`} cohort={cohort} currentUser={currentUser} past />
          ))}
        </div>
      </section>
    )}
  </div>
}

export default MyCourses
